import { combineReducers } from "redux";
import { reducer as formReducer } from "redux-form";
import { i18nReducer } from "react-redux-i18n";

import Config from "./Config/Reducers/Config";
import XsrfToken from "./Security/Reducers/XsrfToken";
import IsAuthenticated from "./Account/Reducers/Authentication";
import GuestAlertStatus from "./Account/Reducers/GuestAlert";
import UserInfo from "./Account/Reducers/UserInfo";
import UserInfoStatus from "./Account/Reducers/UserInfoStatus";
import LoginStatus from "./Account/Reducers/LoginStatus";
import RegisterStatus from "./Account/Reducers/RegisterStatus";
import ChangePasswordStatus from "./Account/Reducers/ChangePasswordStatus";
import PasswordRecoveryStatus from "./Account/Reducers/PasswordRecoveryStatus";
import PasswordResetStatus from "./Account/Reducers/PasswordResetStatus";
import AddressStatus from "./Account/Reducers/AddressStatus";
import Companies from "./Account/Reducers/Companies";
import Categories from "./Catalog/Reducers/Categories";
import CategoryProducts from "./Catalog/Reducers/CategoryProducts";
import Kits from "./Catalog/Reducers/Kits";
import Rentals from "./Catalog/Reducers/Rentals";
import KitQuantity from "./Catalog/Reducers/KitQuantity";
import DeviceQuantity from "./Catalog/Reducers/DeviceQuantity";
import SelectedKitProductId from "./Product/Reducers/SelectedKitProductId";
import SelectedDeviceProductId from "./Product/Reducers/SelectedDeviceProductId";
import NewProducts from "./Catalog/Reducers/NewProducts";
import Devices from "./Catalog/Reducers/Devices";
import VtrackItems from "./Catalog/Reducers/VtrackItems";
import LineHaulItems from "./Catalog/Reducers/LineHaulItems";
import Accessories from "./Catalog/Reducers/Accessories";
import ZebraAccessories from "./Catalog/Reducers/ZebraAccessories";
import ZebraTC26Accessories from "./Catalog/Reducers/ZebraTC26Accessories";
import ZebraTC58Accessories from "./Catalog/Reducers/ZebraTC58Accessories";
import ZebraTC78Accessories from "./Catalog/Reducers/ZebraTC78Accessories";
import PanasonicAccessories from "./Catalog/Reducers/PanasonicAccessories";
import SamsungAccessories from "./Catalog/Reducers/SamsungAccessories";
import UniversalAccessories from "./Catalog/Reducers/UniversalAccessories";
import Plans from "./Catalog/Reducers/Plans";
import Services from "./Catalog/Reducers/Services";
import Cart from "./Cart/Reducers/Cart";
import TempCart from "./Cart/Reducers/TempCart";
import TempCartKitUI from "./Cart/Reducers/TempCartKitUI";
import TempWirelessPlan from "./Cart/Reducers/TempWirelessPlan";
import Wizards from "./Wizard/Reducers/Wizards";
import WizardInitialized from "./Wizard/Reducers/WizardInitialized";
import WizardNumOfSteps from "./Wizard/Reducers/WizardNumOfSteps";
import WizardCurrentStep from "./Wizard/Reducers/WizardCurrentStep";
import PaymentOption from "./Checkout/Reducers/PaymentOption";
import Countries from "./Checkout/Reducers/Countries";
import States from "./Checkout/Reducers/States";
import Addresses from "./Checkout/Reducers/Addresses";
import ActiveBillingAddress from "./Checkout/Reducers/ActiveBillingAddress";
import ActiveShippingAddress from "./Checkout/Reducers/ActiveShippingAddress";
import CustomerPaymentMethods from "./Checkout/Reducers/CustomerPaymentMethods";
import PaymentMethods from "./Checkout/Reducers/PaymentMethods";
import SelectedPaymentMethod from "./Checkout/Reducers/SelectedPaymentMethod";
import ShippingMethods from "./Checkout/Reducers/ShippingMethods";
import OrderSummary from "./Checkout/Reducers/OrderSummary";
import OrderConfirmation from "./Checkout/Reducers/OrderConfirmation";
import Orders from "./Account/Reducers/Orders";
import OrderDetail from "./Account/Reducers/OrderDetail";
import PaymentStatus from "./Checkout/Reducers/PaymentStatus";
import BlockingStatus from "./Status/Reducers/BlockingStatus";
import ServerError from "./Error/Reducers/ServerError";
import UnauthorizedError from "./Error/Reducers/UnauthorizedError";
import ExpiredSessionError from "./Error/Reducers/ExpiredSessionError";
import SelectedProduct from "./Catalog/Reducers/SelectedProduct";
import ChatAgent from "./Chat/Reducers/Agent";
import ChatOpen from "./Chat/Reducers/ChatOpen";
import MessageCount from "./Chat/Reducers/MessageCount";
import MessageList from "./Chat/Reducers/MessageList";
import ChatSession from "./Chat/Reducers/Session";
import CreateAccountModalOpen from "./Modals/Reducers/CreateAccountModalOpen";
import AddAddressModalOpen from "./Modals/Reducers/Address/AddressModalOpen";
import AddAddressModalData from "./Modals/Reducers/Address/AddressModalData";
import LeaseModalOpen from "./Modals/Reducers/LeaseModalOpen";
import ProcessLeaseModalOpen from "./Modals/Reducers/ProcessLease/ProcessLeaseModalOpen";
import ProcessLeaseModalData from "./Modals/Reducers/ProcessLease/ProcessLeaseModalData";
import EligibilityModalOpen from "./Modals/Reducers/EligibilityModalOpen";
import EligibilityModalInitialized from "./Modals/Reducers/EligibilityModalInitialized";
import WizardInProgressModalOpen from "./Modals/Reducers/WizardInProgress/WizardInProgressModalOpen";
import WizardInProgressModalData from "./Modals/Reducers/WizardInProgress/WizardInProgressModalData";
import AlertModalOpen from "./Modals/Reducers/Alert/AlertModalOpen";
import AlertModalData from "./Modals/Reducers/Alert/AlertModalData";
import AlreadyApprovedModalOpen from "./Modals/Reducers/AlreadyApprovedModalOpen";
import CurrentApprovalCode from "./Checkout/Reducers/CurrentApprovalCode";
import ApprovalCodeStatus from "./Checkout/Reducers/ApprovalCodeStatus";
import LeaseStatus from "./Checkout/Reducers/LeaseStatus";
import NivoSlider from "./Metadata/Reducers/NivoSlider";
import Topics from "./Topics/Reducers/Topics";
import TopicDetails from "./Topics/Reducers/TopicDetails";
import Languages from "./Localization/Reducers/Languages";
import Currencies from "./Localization/Reducers/Currencies";
import ActiveLanguage from "./Localization/Reducers/ActiveLanguage";
import ActiveCurrency from "./Localization/Reducers/ActiveCurrency";
import RequestCount from "./Status/Reducers/RequestCountStatus";
import { USER_LOGGED_IN } from "./Account/Actions";
import PanasonicT1Accessories from "./Catalog/Reducers/PanasonicT1Accessories";

const rootReducer = combineReducers({
    config                     : Config,
    xsrfToken                  : XsrfToken,
    isAuthenticated            : IsAuthenticated,
    guestAlertStatus           : GuestAlertStatus,
    userInfo                   : UserInfo,
    userInfoStatus             : UserInfoStatus,
    loginStatus                : LoginStatus,
    registerStatus             : RegisterStatus,
    changePasswordStatus       : ChangePasswordStatus,
    passwordRecoveryStatus     : PasswordRecoveryStatus,
    passwordResetStatus        : PasswordResetStatus,
    addressStatus              : AddressStatus,
    companies                  : Companies,
    categories                 : Categories,
    categoryProducts           : CategoryProducts,
    kits                       : Kits,
    rentals                    : Rentals,
    kitQuantity                : KitQuantity,
    selectedKitProductId       : SelectedKitProductId,
    deviceQuantity             : DeviceQuantity,
    selectedDeviceProductId    : SelectedDeviceProductId,
    newProducts                : NewProducts,
    devices                    : Devices,
    vtrackItems                : VtrackItems,
    lineHaulItems              : LineHaulItems,
    accessories                : Accessories,
    zebraAccessories           : ZebraAccessories,
    zebraTC26Accessories       : ZebraTC26Accessories,
    zebraTC58Accessories       : ZebraTC58Accessories,
    zebraTC78Accessories       : ZebraTC78Accessories,
    panasonicAccessories       : PanasonicAccessories,
    panasonicT1Accessories     : PanasonicT1Accessories,
    samsungAccessories         : SamsungAccessories,
    universalAccessories       : UniversalAccessories,
    plans                      : Plans,
    services                   : Services,
    cart                       : Cart,
    tempCart                   : TempCart,
    tempCartKitUI              : TempCartKitUI,
    tempWirelessPlan           : TempWirelessPlan,
    wizards                    : Wizards,
    wizardInitialized          : WizardInitialized,
    wizardNumOfSteps           : WizardNumOfSteps,
    wizardCurrentStep          : WizardCurrentStep,
    paymentOption              : PaymentOption,
    selectedPaymentMethod      : SelectedPaymentMethod,
    countries                  : Countries,
    states                     : States,
    addresses                  : Addresses,
    activeBillingAddress       : ActiveBillingAddress,
    activeShippingAddress      : ActiveShippingAddress,
    customerPaymentMethods     : CustomerPaymentMethods,
    paymentMethods             : PaymentMethods,
    shippingMethods            : ShippingMethods,
    orderSummary               : OrderSummary,
    orderConfirmation          : OrderConfirmation,
    orders                     : Orders,
    orderDetail                : OrderDetail,
    paymentStatus              : PaymentStatus,
    blockingStatus             : BlockingStatus,
    serverError                : ServerError,
    unauthorizedError          : UnauthorizedError,
    expiredSessionError        : ExpiredSessionError,
    selectedProduct            : SelectedProduct,
    chatAgent                  : ChatAgent,
    chatOpen                   : ChatOpen,
    messageCount               : MessageCount,
    messageList                : MessageList,
    chatSession                : ChatSession,
    createAccountModalOpen     : CreateAccountModalOpen,
    addAddressModalOpen        : AddAddressModalOpen,
    addAddressModalData        : AddAddressModalData,
    leaseModalOpen             : LeaseModalOpen,
    processLeaseModalOpen      : ProcessLeaseModalOpen,
    processLeaseModalData      : ProcessLeaseModalData,
    eligibilityModalOpen       : EligibilityModalOpen,
    eligibilityModalInitialized: EligibilityModalInitialized,
    wizardInProgressModalOpen  : WizardInProgressModalOpen,
    wizardInProgressModalData  : WizardInProgressModalData,
    alertModalOpen             : AlertModalOpen,
    alreadyApprovedModalOpen   : AlreadyApprovedModalOpen,
    alertModalData             : AlertModalData,
    currentApprovalCode        : CurrentApprovalCode,
    approvalCodeStatus         : ApprovalCodeStatus,
    leaseStatus                : LeaseStatus,
    nivoSlider                 : NivoSlider,
    topics                     : Topics,
    topicDetails               : TopicDetails,
    languages                  : Languages,
    currencies                 : Currencies,
    activeLanguage             : ActiveLanguage,
    activeCurrency             : ActiveCurrency,
    requestCount               : RequestCount,
    i18n                       : i18nReducer,
    form                       : formReducer
    // form: formReducer.plugin({
    //     LoginForm: (state, action) => { // <------ 'account' is name of form given to reduxForm()
    //         switch(action.type) {
    //             case USER_LOGGED_IN:
    //                 return undefined;       // <--- blow away form data
    //             default:
    //                 return state;
    //         }
    //     }
    // })
});

export default rootReducer;